import type { MouseEvent } from 'react';
import { useState } from 'react';
import StatusCode from 'status-code-enum';

import { accountsApi } from '../../../api/accountsApi';

import { fireToast } from '../../../helpers/Toasts';
import { useOnUpdateOnly } from '../../../hooks/UseOnUpdateOnly';
import type { useSaveAdArgs } from '../../../components/Support/UseSaveAd/useSaveAd.typed';

import { useBraze } from '../../../hooks/useBraze';
import { pushToDataLayer } from '../../../services/gtmService';
import { useAuthOptions } from '../../../contexts/UserContext';
import { formatAuthorizationHeader } from '../../../helpers/auth';
import { AUTHENTICATION_STATUSES } from '../../PageWrappers/AdDetails/types';

function useSaveAd(args: useSaveAdArgs) {
  const {
    adId,
    userSaved,
    section,
    sellerType,
    trackCreateSaveAd,
    trackDeleteSaveAd,
    sectionName,
    isFreeHistoryCheckAd = false,
    category,
    location,
    price,
  } = args;
  const [isAdSaved, setIsAdSaved] = useState(userSaved);
  const [isSaving, setIsSaving] = useState(false);

  const [queueSaveAd, setQueueSaveAd] = useState(false);
  const { logBrazeSaveAdEvent, logBrazeFreeHistoryCheckAdSave } = useBraze();

  const { handleLogin, status, accessToken } = useAuthOptions();

  const authorizationHeader = formatAuthorizationHeader(accessToken);

  const handleRequest = async (savedStatus: boolean) => {
    try {
      const { status } = savedStatus
        ? await accountsApi.unsaveAd(adId, authorizationHeader)
        : await accountsApi.saveAd(adId, authorizationHeader);

      const requestStatus = status === StatusCode.SuccessOK;
      if (requestStatus) {
        if (!savedStatus) {
          fireToast({
            type: 'SUCCESS',
            text: 'Added to your Saved Ads',
          });
        } else {
          fireToast({
            type: 'SUCCESS',
            text: 'Removed from your Saved Ads',
          });
        }
      }
      return requestStatus;
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return false;
    }
  };

  const triggerToggleSave = async () => {
    setIsSaving(true);
    const isOk = await handleRequest(isAdSaved);
    setIsSaving(false);
    if (isOk) {
      setIsAdSaved && setIsAdSaved(!isAdSaved);
      if (!isAdSaved) {
        if (trackCreateSaveAd) {
          trackCreateSaveAd(adId ?? 0);
        } else {
          pushToDataLayer({
            event: 'create_saved_ad',
          });
        }

        if (isFreeHistoryCheckAd) {
          logBrazeFreeHistoryCheckAdSave({
            ad_id: adId.toString(),
            category: category ?? '',
            location: location ?? '',
            ad_price: price ?? '',
          });
        } else {
          logBrazeSaveAdEvent(
            trackCreateSaveAd ? sectionName : section,
            sellerType,
          );
        }
      } else {
        if (trackDeleteSaveAd) {
          trackDeleteSaveAd();
        } else {
          pushToDataLayer({
            event: 'delete_saved_ad',
          });
        }
      }
    }
  };

  const loginAndSave = () => {
    setQueueSaveAd(true);
    handleLogin && handleLogin();
  };

  const onSave = (event: MouseEvent) => {
    event.preventDefault(); // Do no trigger the link if one is wrapped around this component
    event.stopPropagation(); // Do not trigger the click handler if one is included in the parent component
    status === AUTHENTICATION_STATUSES.AUTHENTICATED
      ? triggerToggleSave()
      : loginAndSave();
  };

  useOnUpdateOnly(() => {
    setIsAdSaved(userSaved);
  }, [adId]);

  useOnUpdateOnly(() => {
    if (status === AUTHENTICATION_STATUSES.UNAUTHENTICATED && isAdSaved) {
      setIsAdSaved(false);
    }
  }, [status]);

  return {
    isAdSaved,
    queueSaveAd,
    onSave,
    triggerToggleSave,
    setIsAdSaved,
    isSaving,
  };
}

export { useSaveAd };
